import React, { useState, useEffect } from "react"
import { ExtractId } from "../../../util"
import Paginator from "../../Paginator"
import { getManageFacilityList } from "../../../api/manage_facility_list"
import { useNavigate } from "react-router-dom"
import { useLoading } from "../../LoadingContext"
import { useConnectionError } from "../../ConnectionErrorContext"

import ManageFacilityCreate from "./create"
import ManageFacilityUpdate from "./update"
import ManageFacilityDelete from "./delete"

import DashboardLayout from "../../components/DashboardLayout"
import DashboardNavbar from "../../components/DashboardNavbar"
import Icon from "@mui/material/Icon"
import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import Button from "@mui/material/Button"
import MDInput from "components/MDInput"
import MDButton from "components/MDButton"

export interface ManageFacilityData {
  id: string
  catv_id: string
  estate_id: string
  type: string
  name: string
  address: string
  phone: string
  building_type: number
  create_date: number
  update_date: number
  is_delete: number
  medical_records: number
}

const ManageFacilityTileList: React.FC = () => {
  const { startLoading, stopLoading } = useLoading()
  const { showConnectionError } = useConnectionError()
  const navigate = useNavigate()

  const [searchText, setSearchText] = useState("")
  const [facilityData, setFacilityData] = useState<ManageFacilityData[]>([])
  const [filteredData, setFilteredData] = useState<ManageFacilityData[]>([])
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 25

  const [facilityCreate, setFacilityCreate] = useState<boolean>(false)
  const [facilityEdit, setFacilityEdit] = useState<boolean>(false) // 編集モーダルの表示/非表示
  const [facilityDelete, setFacilityDelete] = useState<boolean>(false) // 編集モーダルの表示/非表示
  const [selectedFacility, setSelectedFacility] = useState<ManageFacilityData | null>(null) // 編集対象のサービス提供元データ
  const [showEditButton, setShowEditButton] = useState<boolean>(false)

  // 開発者アカウントでの閲覧を可能とする
  const [isViewMode, setIsViewMode] = useState<boolean>(false)

  if (localStorage.getItem("session_code") == "") {
    navigate("/login")
    return <></>
  }

  const paginatedData = filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)

  const fetchManageFacilityList = async () => {
    try {
      startLoading()
      const manageFacilityData = await getManageFacilityList()

      // 開発者アカウントでの閲覧を可能とする
      const filteredFacilityData = manageFacilityData.facility_list
      setFacilityData(filteredFacilityData)

      // 開発者アカウントでの閲覧を可能とする
      const auth = localStorage.getItem("account_type")
      if (auth == "service") {
        let isViewModeWork = true
        const view_mode = localStorage.getItem("view_mode")
        if (view_mode == "2") {
          const estate_id = localStorage.getItem("show_mode_estate")
          if (estate_id !== "" && estate_id !== "-") {
            isViewModeWork = false
            setShowEditButton(true)
          }
        }
        setIsViewMode(isViewModeWork)
      }
    } catch (error: any) {
      if (error.response && error.response.status === 401) {
        navigate("/login")
      } else if (!error.response) {
        showConnectionError(true)
      }
    } finally {
      stopLoading()
    }
  }
  useEffect(() => {
    fetchManageFacilityList()
  }, [])

  useEffect(() => {
    const auth = localStorage.getItem("auth_facility")
    if (auth === "1") {
      setShowEditButton(true)
    }
  }, [])

  // 検索処理
  const handleSearch = () => {
    const filtered = facilityData.filter((facility) => {
      const matchesText =
        searchText === "" ||
        facility.name?.includes(searchText) ||
        facility.address?.includes(searchText) ||
        facility.phone?.includes(searchText)

      return matchesText
    })
    setFilteredData(filtered)
    setCurrentPage(1)
  }

  // 初期表示時に初期ページを表示
  useEffect(() => {
    // ダミーデータを用意してページャーを確認する用
    /*
    const expandedData = Array.from({ length: 100 }, (_, i) =>
      facilityData.map((item, index) => ({
        ...item,
        id: `${item.id}-copy${i}`, // ID を一意にする
        name: `${item.name} (${i + 1})`, // 施設名に番号を付与
      }))
    ).flat(); // 2件 * 100 = 200件
    setFilteredData(expandedData);
    */

    setFilteredData(facilityData)
  }, [facilityData])

  const handleCreate = () => {
    //console.log("追加ボタンがクリックされた ");
    setFacilityCreate(true)
  }

  const handleEdit = (id: string) => {
    //console.log("編集ボタンがクリックされた " + id);
    const facilityToEdit = facilityData.find((facility) => facility.id === id)
    if (facilityToEdit) {
      setSelectedFacility(facilityToEdit) // 編集対象をセット
      setFacilityEdit(true) // 編集モーダルを表示
    }
  }
  const handleDelete = (id: string) => {
    //console.log("削除ボタンがクリックされた " + id);
    const facilityToEdit = facilityData.find((facility) => facility.id === id)
    if (facilityToEdit) {
      setSelectedFacility(facilityToEdit) // 編集対象をセット
      setFacilityDelete(true) // 編集モーダルを表示
    }
  }

  const handleClose = async (num: number) => {
    if (num === 1) {
      // APIを再取得
      await fetchManageFacilityList()

      // データ更新後に検索を行う
      setFacilityCreate(false)
      setFacilityEdit(false)
      setFacilityDelete(false)

      // フィルタリングを再実行して、画面を更新
      handleSearch()
    } else {
      setFacilityCreate(false)
      setFacilityEdit(false)
      setFacilityDelete(false)
    }
  }

  const totalPages = Math.ceil(filteredData.length / itemsPerPage)

  return (
    <DashboardLayout>
      {facilityCreate && <ManageFacilityCreate onClose={handleClose} />}
      {facilityEdit && selectedFacility && (
        <ManageFacilityUpdate onClose={handleClose} facilityData={selectedFacility} />
      )}
      {facilityDelete && selectedFacility && (
        <ManageFacilityDelete onClose={handleClose} facilityData={selectedFacility} />
      )}
      <MDBox mb={4}>
        <DashboardNavbar absolute={true} />
      </MDBox>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" pt={3} pb={3}>
        <MDBox>
          <MDTypography variant="h4" gutterBottom>
            施設一覧
          </MDTypography>
        </MDBox>
      </MDBox>
      {/* 検索フォーム */}
      <MDBox
        mb={2}
        display="flex"
        gap={3}
        flexWrap="wrap"
        alignItems="center"
        sx={{ fontSize: "0.875rem", color: "text.primary" }}
      >
        <MDBox display="flex" alignItems="center" gap={1}>
          <MDInput
            label="施設名や住所で検索"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            sx={{ width: "240px", backgroundColor: "#FFFFFF", borderRadius: "10px" }}
          />
        </MDBox>
        <MDButton variant="gradient" color="info" size="medium" onClick={handleSearch}>
          <Icon>search</Icon>&nbsp; 検索
        </MDButton>
        <MDBox sx={{ width: "calc(100% - 482px)" }} />
        {!isViewMode && (
          <>
            <MDBox>
              {showEditButton && (
                <MDButton variant="gradient" color="info" size="medium" onClick={handleCreate}>
                  追加
                </MDButton>
              )}
            </MDBox>
          </>
        )}
      </MDBox>
      <MDBox>
        <TableContainer sx={{ overflowX: "auto", overflowY: "auto", maxHeight: "calc(100vh - 300px)" }}>
          <Table stickyHeader>
            <TableHead sx={{ position: "sticky", display: "table-header-group", zIndex: 3 }}>
              <TableRow
                sx={{
                  "& .MuiTableCell-root": {
                    fontSize: "0.875rem",
                    color: "text.primary",
                    fontWeight: "bold",
                    backgroundColor: "#FFFFFF"
                  }
                }}
              >
                <TableCell align="center">施設ID</TableCell>
                <TableCell align="left">施設名</TableCell>
                <TableCell align="left">住所</TableCell>
                <TableCell align="left">電話番号</TableCell>
                <TableCell align="left">建物種別</TableCell>
                {!isViewMode && (
                  <>
                    <TableCell align="center" style={{ minWidth: "75px" }}>
                      編集
                    </TableCell>
                    <TableCell align="center" style={{ minWidth: "75px" }}>
                      削除
                    </TableCell>
                  </>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.map((row, index) => (
                <TableRow
                  key={index}
                  sx={{
                    "& .MuiTableCell-root": {
                      fontSize: "0.875rem",
                      color: "text.primary",
                      fontWeight: "normal",
                      zIndex: 0
                    }
                  }}
                >
                  <TableCell align="center">
                    <div
                      style={{
                        minWidth: "50px",
                        maxWidth: "300px",
                        width: "10vw",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis"
                      }}
                      title={ExtractId(row.id) || undefined}
                    >
                      {ExtractId(row.id)}
                    </div>
                  </TableCell>
                  <TableCell align="left">
                    <div style={{ minWidth: "200px" }}>{row.name}</div>
                  </TableCell>
                  <TableCell align="left">
                    <div style={{ minWidth: "200px" }}>{row.address}</div>
                  </TableCell>
                  <TableCell align="left">
                    <div style={{ minWidth: "100px" }}>{row.phone}</div>
                  </TableCell>
                  <TableCell align="left">
                    <div style={{ minWidth: "100px" }}>{row.building_type === 0 ? "マンション" : "アパート"}</div>
                  </TableCell>
                  {!isViewMode && (
                    <>
                      <TableCell align="right">
                        {showEditButton && (
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            sx={{ color: "#FFF" }}
                            onClick={() => {
                              handleEdit(`${row.id}`)
                            }}
                          >
                            編集
                          </Button>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {showEditButton && row.medical_records == 0 && (
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            sx={{ color: "#FFF" }}
                            onClick={() => {
                              handleDelete(`${row.id}`)
                            }}
                          >
                            削除
                          </Button>
                        )}
                      </TableCell>
                    </>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </MDBox>
      {/* ページャー */}
      <Paginator
        key={"ManageFacilityTileListPaginator"}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
    </DashboardLayout>
  )
}

export default ManageFacilityTileList
